import React, { useContext, useEffect, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import loadable from '@loadable/component'
import { LayoutContext, useUI } from '../../contexts'
import useShopifyProduct from '../../hooks/useShopifyProduct'
import useReplaceLineItem from '../../hooks/useReplaceLineItem'
import { decode } from 'shopify-gid'
import adviceSize from './SizeWizard/adviceSize'
import setSizeVariants from './SizeWizard/setSizeVariants'
import AddDiscountToCart from '../shared/AddDiscountToCart'
import { getProductFormPrice } from '../CurrencyConverterSwitcher'
import Price from '../Price'
import { Icon } from '../shared/Icon'
import { Portal } from '../Portal'
import { desktopVW, mobileVW } from '../../styles'
import {
  useProductViewedTracking,
  useProductAddedTracking,
  useSizeVariantSelectedTracking,
  useProductInfoClickedTracking,
  useAmazonButtonClickedTracking,
} from '../../hooks/tracking'
import { useShopify } from '../../contexts/shopify'
import { useGlobal } from '../../hooks/useGlobal'
import {
  getCookieKeyfbc,
  getCookieKeyfbp,
  getCookieValue,
  getFbc,
  getSegmentAnonymousId,
  getSegmentAnonymousIdKey,
} from '../../utils'
import useUpdateCheckoutAttributes from '../../hooks/useUpdateCheckoutAttributes'

const RichText = loadable(() => import('../RichText'))
const SizeWizzard = loadable(() => import('./SizeWizard/SizeWizzard'))
const BackInStockForm = loadable(() => import('./BackInStockForm'))

const ProductForm = ({
  name,
  title,
  handle,
  handle2,
  size_select,
  shopifyProduct,
  sizeSelect,
  productType,
  gender,
  color,
  onSizeTableOpen,
  location,
  outOfStockMessages,
  thumbnailImage,
  productSlug,
  family,
  variants,
  setVariants,
  selectedVariant,
  setSelectedVariant,
  sizeIndex,
  setSizeIndex,
  amazonUrl,
  amazonPriceValue,
  notComingBack,
  sizeInfo,
  removeIndividualTieredDiscountMessage, // this is not working right now, only returns NULL no time to debug now
  preorder,
  preorderMessage, // If "continue selling" is active, the preorderMessage field in the Product entry will show
}) => {
  // ---------------------- variables & functions -------------------------
  const { node_locale, currency, storeSubdomain } = useContext(LayoutContext)
  const [updateCheckout, { customAttributes }] = useUpdateCheckoutAttributes()
  const isSkiing = family?.name.toLowerCase().includes('skiing')

  const {
    product: {
      addToCart,
      addingToCartLoading,
      size,
      outOfStock,
      inStock,
      sizeWizard: sizeWizzard,
      almostOutOfStock,
      germanLegal,
      amazonButtonText,
      tieredDiscountMessage,
      showTieredDiscountMessage,
      showProductBundle,
      outOfStockATC,
      notComingBackATC,
      preorderATC,
    },
    blackFriday: {
      colorStrikethrough,
      productColorStrikethrough,
      blackFridayThemeColor,
      showAnnouncementBarBlackFriday2022,
      bFDiscountMessage2022,
    },
    cart: { showSpringSaleTieredDiscountMessage },
  } = useGlobal(['product', 'blackFriday', 'cart'], node_locale)

  const [discountPrice, setDiscountPrice] = useState('')
  const [originalPrice, setOriginalPrice] = useState('')

  const { setCartOpen, sizeWizardOpen, setSizeWizardOpen } = useUI()

  const { checkouts: allCheckouts, replaceSizeVariants } = useShopify()

  let { product } = useShopifyProduct(handle, storeSubdomain)
  // secondary shopify product for out of stock fallback
  const { product: product2 } = useShopifyProduct(handle2)

  product = product ?? product2

  const checkouts = allCheckouts[storeSubdomain]
  const [replaceLineItem, { loading }] = useReplaceLineItem()

  const [bigDiscount, setBigDiscount] = useState(false)

  useEffect(() => {
    setBigDiscount(
      checkouts?.lineItems?.edges
        .filter((edge) => !edge?.node?.title.toLowerCase().includes('skiing'))
        .filter((edge) => !edge?.node?.title.toLowerCase().includes('gift'))
        .length > 0,
    )
  }, [checkouts.lineItems.edges.length])

  const {
    sizeVariants,
    minAnkleSizeValue,
    maxAnkleSizeValue,
    defaultAnkleSizeValue,
    defaultCalfRange,
    defaultLowerLegRange,
    minShoeSizeValue,
    maxShoeSizeValue,
    defaultShoeSizeValue,
  } = setSizeVariants(gender, productType, node_locale)

  const getAdminIdFromStorefrontId = (storefrontId) =>
    window.atob(storefrontId).match(/\d/g).join('')

  // const sizeWizzardOpen = true // for testing
  const onCloseSizeWizard = () => setSizeWizardOpen(false)
  const onOpenSizeWizard = () => {
    let measurements = []
    for (let measurement in selectedOptions) {
      if (selectedOptions[measurement]) measurements.push(measurement)
    }

    setSizeWizardOpen(true)
  }

  const outOfStockMessagePerSize =
    product &&
    outOfStockMessages &&
    outOfStockMessages.find((message) => message.size === selectedSize) &&
    outOfStockMessages.find((message) => message.size === selectedSize).message

  const giftProducts =
    product?.handle === 'gift-wrapping' || product?.handle === 'stox-gift-card'

  const tieredDiscountExclusions =
    product?.title === 'Washing Bag' || giftProducts

  // if only gift products, don't apply the tiered discount to the current product's add-to-cart-button
  const giftProductsInCart = checkouts.lineItems.edges.filter(({ node }) => {
    const prodHandle = node?.variant?.product?.handle
    return prodHandle === 'stox-gift-card' || prodHandle === 'gift-wrapping'
  })
  const onlyGiftProductsInCart =
    giftProductsInCart.length &&
    giftProductsInCart.length === checkouts.lineItems.edges.length

  const getPrice = (slug) => {
    const priceEl = document.querySelector(
      `.add-to-cart .product-price.${slug}`,
    )

    if (!priceEl) {
      return null
    } else {
      const amount = Number(
        priceEl.textContent.replace(',', '.').replace(currency?.symbol, ''),
      )
      return amount
    }
  }

  // -------------------------- states ----------------------------------
  const [quantity, setQuantity] = useState(1)
  const [addedQuantity, setAddedQuantity] = useState(1)
  const [availibility, setAvailibility] = useState(true)
  const [lineItem, setLineItem] = useState(null)
  const [selectedSize, setSelectedSize] = useState()
  const [variantQuantity, setVariantQuantity] = useState()
  // - - - - - - - - - - - -  size wizzard states - - - - - - - - - - - - - -
  const [defaultAnkleSize, setDefaultAnkleSize] = useState(
    defaultAnkleSizeValue,
  )
  const [defaultShoeSize, setDefaultShoeSize] = useState(defaultShoeSizeValue)
  const [selectedCalfSizeIndex, setSelectedCalfSizeIndex] = useState(0)
  const [selectedLowerLegSizeIndex, setSelectedLowerLegSizeIndex] = useState(0)
  const [selectedOptions, setSelectedOptions] = useState({
    ankle: defaultAnkleSize,
    calf: defaultCalfRange,
    lowerLeg: defaultLowerLegRange,
    shoe: defaultShoeSize,
  })
  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  const [showDiscount, discountAmount] = getProductFormPrice(
    node_locale,
    quantity,
    checkouts,
  )
  const [showBISForm, updateShowBISForm] = useState(false)

  // ------------------ session storage sizes (remember user size feature) --------------------------

  const sessionSizeVariantObj = useShopify().sizeVariants[productType.name]

  const sessionSizeVariantIndex =
    sessionSizeVariantObj && sessionSizeVariantObj[gender.name]
  const defaultSizeVariantIndex = sessionSizeVariantIndex
    ? sessionSizeVariantIndex
    : 0

  // -------------------------- Initializing shopify product ---------------------------------
  // We have a feature for a fallback secondary shopify product variant
  // if that variant of the primary shopify product runs out of stock
  useEffect(() => {
    if (!product || (!product && !product2)) {
      return
    }

    // if there is a secondary product
    if (handle2 && product2 && product) {
      const primaryProductVariants = product.variants.edges
      const secondaryProductVariants = product2.variants.edges
      // a function to check if sec product variants are input properly
      function variantsMatch(primaryProductVariants, secondaryProductVariants) {
        if (primaryProductVariants.length !== secondaryProductVariants.length)
          return false
        for (let i = 0; i < primaryProductVariants.length; i++) {
          if (
            primaryProductVariants[i].node.title !==
            secondaryProductVariants[i]?.node.title
          )
            return false
        }
        return true
      }

      // if primary product variants are all in stock
      // or if sec product is not input properly
      if (
        primaryProductVariants.every(
          ({ node: { availableForSale } }) => availableForSale === true,
        ) ||
        !variantsMatch(primaryProductVariants, secondaryProductVariants)
      ) {
        setVariants(primaryProductVariants)
        setSelectedVariant(
          primaryProductVariants[defaultSizeVariantIndex]?.node.id,
        )
        setSizeIndex(defaultSizeVariantIndex)
        setSelectedSize(
          primaryProductVariants[defaultSizeVariantIndex]?.node.title,
        )
        setAvailibility(
          primaryProductVariants[defaultSizeVariantIndex]?.node
            .availableForSale,
        )
        setVariantQuantity(
          primaryProductVariants[defaultSizeVariantIndex]?.node
            ?.quantityAvailable,
        )
        setLineItem(
          checkouts.lineItems.edges.find(
            ({
              node: {
                variant: { id },
              },
            }) =>
              id === primaryProductVariants[defaultSizeVariantIndex].node.id,
          ),
        )
        // some primary product variants are out of stock
      } else {
        let productVariants = primaryProductVariants
        // returns an array of indices of the variants that are out of stock
        const indices = primaryProductVariants
          .map(({ node: { availableForSale } }, index) =>
            !availableForSale ? index : '',
          )
          .filter(String)
        // replace pri product variant that is out of stock with sec product variant
        // if sec product variant is also out of stock, go back to pri product variant
        indices.forEach((index) => {
          productVariants.splice(
            index,
            1,
            secondaryProductVariants[index].node.availableForSale
              ? secondaryProductVariants[index]
              : primaryProductVariants[index],
          )
        })
        setVariants(productVariants)
        setLineItem(
          checkouts.lineItems.edges.find(
            ({
              node: {
                variant: { id },
              },
            }) => id === productVariants[defaultSizeVariantIndex].node.id,
          ),
        )
        // if the first variant is still out of stock and only 1 product variant
        if (
          !productVariants[defaultSizeVariantIndex].node.availableForSale &&
          primaryProductVariants.length === 1
        ) {
          setSelectedVariant(productVariants[0].node.id)
          setSelectedSize(productVariants[0].node.title)
          setAvailibility(productVariants[0].node.availableForSale)
          setVariantQuantity(productVariants[0].node.quantityAvailable)
          setSizeIndex(0)
        } else if (
          !productVariants[defaultSizeVariantIndex].node.availableForSale
        ) {
          setSelectedVariant(productVariants[1].node.id)
          setSelectedSize(productVariants[1].node.title)
          setAvailibility(productVariants[1].node.availableForSale)
          setVariantQuantity(productVariants[1].node.quantityAvailable)
          setSizeIndex(1)
        } else {
          setSelectedVariant(productVariants[defaultSizeVariantIndex].node.id)
          setSizeIndex(defaultSizeVariantIndex)
          setSelectedSize(productVariants[defaultSizeVariantIndex].node.title)
          setAvailibility(
            productVariants[defaultSizeVariantIndex].node.availableForSale,
          )
          setVariantQuantity(
            productVariants[defaultSizeVariantIndex].node.quantityAvailable,
          )
        }
      }

      // if there is no secondary product
    } else if (product && !product2) {
      const primaryProductVariants = product.variants.edges
      setVariants(primaryProductVariants)
      setLineItem(
        checkouts.lineItems.edges.find(
          ({
            node: {
              variant: { id },
            },
          }) => id === primaryProductVariants[defaultSizeVariantIndex]?.node.id,
        ),
      )

      // if there is only 1 variant & is out of stock

      if (
        !primaryProductVariants[defaultSizeVariantIndex]?.node
          .availableForSale &&
        primaryProductVariants.length === 1
      ) {
        setSelectedVariant(primaryProductVariants[0].node.id)
        setSelectedSize(primaryProductVariants[0].node.title)
        setAvailibility(primaryProductVariants[0].node.availableForSale)
        setVariantQuantity(primaryProductVariants[0].node.quantityAvailable)
        setSizeIndex(0)
      } else if (
        !primaryProductVariants[defaultSizeVariantIndex]?.node.availableForSale
      ) {
        setSelectedVariant(primaryProductVariants[1].node.id)
        setSelectedSize(primaryProductVariants[1].node.title)
        setAvailibility(primaryProductVariants[1].node.availableForSale)
        setVariantQuantity(primaryProductVariants[1].node.quantityAvailable)
        setSizeIndex(1)
      } else {
        setSelectedVariant(
          primaryProductVariants[defaultSizeVariantIndex]?.node.id,
        )
        setSizeIndex(defaultSizeVariantIndex)
        setSelectedSize(
          primaryProductVariants[defaultSizeVariantIndex]?.node.title,
        )
        setAvailibility(
          primaryProductVariants[defaultSizeVariantIndex]?.node
            .availableForSale,
        )
        setVariantQuantity(
          primaryProductVariants[defaultSizeVariantIndex]?.node
            .quantityAvailable,
        )
      }
    }
  }, [product, product2, checkouts.lineItems.edges])

  useEffect(() => {
    if (checkouts.lineItems) {
      setLineItem(
        checkouts.lineItems.edges.find(
          ({
            node: {
              variant: { id },
            },
          }) => id === selectedVariant,
        ),
      )
    }
  }, [selectedVariant, checkouts.lineItems])

  useEffect(() => {
    if (lineItem) {
      setAddedQuantity(lineItem.node.quantity)
    } else {
      setAddedQuantity(0)
    }
  }, [lineItem])

  // ---------------------- button size select handle --------------------------
  const buttonSizeSelectHandle = (node, index) => {
    setSelectedVariant(node.id)
    setAvailibility(node.availableForSale)
    setSelectedSize(node.title)
    setSizeIndex(index)
    setVariantQuantity(node.quantityAvailable)
    replaceSizeVariants(productType.name, gender.name, index)
    useSizeVariantSelectedTracking({
      currency: currency?.code,

      locale: node_locale,
      name: title,
      variant: node.title,
      family: family.name,
      sku: node.sku,
      price: getPrice(productSlug),
      sectionType: 'Product Form',
    })
  }

  // --------------------- dropdown size select handle -----------------------
  const dropdownSizeSelectHandle = (e) => {
    e.preventDefault()
    setSelectedVariant(e.target.value.split(',')[0])
    setAvailibility(e.target.value.split(',')[1] === 'true')
    setSelectedSize(e.target.value.split(',')[2])
    setSizeIndex(Number(e.target.value.split(',')[3]))

    replaceSizeVariants(
      productType.name,
      gender.name,
      parseInt(e.target.value.split(',')[3]),
    ),
      setVariantQuantity(parseInt(e.target.value.split(',')[4]))
    useSizeVariantSelectedTracking({
      currency: currency?.code,

      locale: node_locale,
      name: title,
      variant: e.target.value.split(',')[2],
      family: family.name,
      sku: e.target.value.split(',')[5],
      price: getPrice(productSlug),
      sectionType: 'Product Form',
    })
  }

  // ------------------------------ add to cart -----------------------------------
  const addProductToCart = async (variantId, quantity) => {
    await replaceLineItem({
      variantId: variantId,
      quantity: quantity,
    })

    setCartOpen(true)

    useProductAddedTracking({
      currency: currency?.code,

      locale: node_locale,
      checkoutId: checkouts.id && decode(checkouts.id).id,
      // productId: product && decode(product.id).id,
      // variantId: decode(selectedVariant).id,
      productId: selectedVariant && decode(selectedVariant).id,
      sku: variants[sizeIndex].node.sku,
      name: title,
      variant: variants[sizeIndex].node.selectedOptions[0].value,
      price: product?.variants.edges[0].node.presentmentPrices.edges.find(
        ({
          node: {
            price: { currencyCode },
          },
        }) => currencyCode === currency.code,
      ).node.price.amount,
      quantity: quantity - addedQuantity,
      value: getPrice(productSlug),
      thumbnailImage,
      viewType: 'Product Page',
      sectionType: 'Product Form',
    })
  }

  // -------------------------- product viewed tracking -------------------------------
  // primary product
  useEffect(() => {
    // gtm
    if (selectedVariant && typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'productDetailView',
        ecommerce: {
          currencyCode: 'EUR',
          detail: {
            products: [
              {
                id: getAdminIdFromStorefrontId(selectedVariant),
                sku: variants[sizeIndex].node.sku,
                slug: location.pathname,
                name: title,
                variant: variants[sizeIndex].node.selectedOptions[0].value,
                price:
                  variants[sizeIndex].node.presentmentPrices.edges[0].node.price
                    .amount,
                quantity: 1,
                currency: 'EUR',
                brand: 'Stox',
                category: 'Socks',
              },
            ],
          },
        },
      })
    }

    // segment
    if (selectedVariant) {
      // wait a moment until firing getPrice
      // so that the dom element is loaded

      /**
       * Currently we're using the variantId as the
       * productId, in the tracking - this is what
       * STOX initially requested
       */

      setTimeout(() => {
        useProductViewedTracking({
          currency: currency?.code,

          locale: node_locale,
          // productId: product && decode(product.id).id,
          // variantId: selectedVariant && decode(selectedVariant).id,
          productId: selectedVariant && decode(selectedVariant).id,
          sku: variants[sizeIndex].node.sku,
          name: title,
          variant: variants[sizeIndex].node.selectedOptions[0].value,
          color: color.title,
          price: getPrice(productSlug),
          thumbnailImage,
        })
      }, 1500)
    }
  }, [selectedVariant])

  useEffect(() => {
    if (product) {
      const originalPrice =
        product.variants.edges[0].node.presentmentPrices.edges[0].node
          .compareAtPrice

      if (originalPrice) {
        setOriginalPrice(
          parseFloat(
            product.variants.edges[0].node.presentmentPrices.edges.find(
              ({
                node: {
                  compareAtPrice: { currencyCode },
                },
              }) => currencyCode === currency.code,
            ).node.compareAtPrice.amount,
          ),
        )
        setDiscountPrice(
          parseFloat(
            product.variants.edges[0].node.presentmentPrices.edges.find(
              ({
                node: {
                  price: { currencyCode },
                },
              }) => currencyCode === currency.code,
            ).node.price.amount,
          ),
        )
      } else {
        setDiscountPrice(
          parseFloat(
            product.variants.edges[0].node.presentmentPrices.edges.find(
              ({
                node: {
                  price: { currencyCode },
                },
              }) => currencyCode === currency.code,
            ).node.price.amount,
          ),
        )
      }
    }
  }, [product])

  // --------------------------------------------------------------------------
  const serversideTrack = async () => {
    const segmentId = getSegmentAnonymousId()

    if (segmentId) {
      // update checkout attributes with the segmentId and facebook cookies
      const response = await updateCheckout({
        customAttributes: [
          {
            key: getSegmentAnonymousIdKey(),
            value: segmentId,
          },
          {
            key: getCookieKeyfbc(),
            value: getCookieValue('_fbc') || getFbc() || '',
          },
          {
            key: getCookieKeyfbp(),
            value: getCookieValue('_fbp'),
          },
        ],
      })
    }
  }

  const handleAddToCart = () => {
    serversideTrack()

    return availibility
      ? addProductToCart(selectedVariant, addedQuantity + quantity)
      : (updateShowBISForm(true),
        useProductInfoClickedTracking({
          currency: currency?.code,
          locale: node_locale,
          name: title,
          family: family.name,
          viewType: 'Product Page',
          productInfoModule: 'Back In Stock',
          linkType: 'Button',
        }))
  }

  const getDefaultVariantValue = () => {
    if (selectedVariant) {
      const variant = variants?.find(({ node }) => node.id === selectedVariant);
      const i = variants?.indexOf(variant);
      const { node: { id, availableForSale, title, quantityAvailable, sku } } = variant
      return `${id},${availableForSale},${title},${i},${quantityAvailable},${sku}`
    }
    return ''
  }

  return (
    <>
      <AddDiscountToCart location={location} />
      <SizePicker>
        <SizeWrapper>
          <span>{size}:</span>
          <ul>
            {sizeSelect ? (
              <SizeSelectWrapper>
                <SizeSelect onChange={dropdownSizeSelectHandle} value={getDefaultVariantValue()}>
                  {variants.map(({ node }, i) => {
                    return (
                      <option
                        key={node.id}
                        value={[
                          node.id,
                          node.availableForSale,
                          node.title,
                          i,
                          node.quantityAvailable,
                          node.sku,
                        ]}
                      >
                        {node.selectedOptions?.find(opt => opt?.name?.toLowerCase() === 'size')?.value}
                      </option>
                    )
                  })}
                </SizeSelect>
              </SizeSelectWrapper>
            ) : (
              variants.map(({ node }, i) => (
                <SizeListItem key={i} selected={node.id === selectedVariant}>
                  <Size
                    active={node.id === selectedVariant}
                    type='button'
                    aria-label='size'
                    onClick={() => buttonSizeSelectHandle(node, i)}
                  >
                    {node.selectedOptions?.find(opt => opt?.name?.toLowerCase() === 'size')?.value}
                  </Size>
                </SizeListItem>
              ))
            )}
            {productType.name === 'Other' ? null : (
              <SizeTableButton
                type='button'
                onClick={sizeInfo ? onSizeTableOpen : onOpenSizeWizard}
              >
                {sizeWizzard}
              </SizeTableButton>
            )}
          </ul>
          {!sizeInfo && (
            <SizeWizzard
              onCloseSizeWizard={onCloseSizeWizard}
              genderProp={gender.name}
              sizeVariants={sizeVariants}
              // ankle size
              setDefaultAnkleSize={setDefaultAnkleSize}
              defaultAnkleSize={defaultAnkleSize}
              minAnkleSize={minAnkleSizeValue}
              maxAnkleSize={maxAnkleSizeValue}
              // shoe size
              setDefaultShoeSize={setDefaultShoeSize}
              defaultShoeSize={defaultShoeSize}
              minShoeSize={minShoeSizeValue}
              maxShoeSize={maxShoeSizeValue}
              // calf
              selectedCalfSizeIndex={selectedCalfSizeIndex}
              setSelectedCalfSizeIndex={setSelectedCalfSizeIndex}
              // lower leg
              selectedLowerLegSizeIndex={selectedLowerLegSizeIndex}
              setSelectedLowerLegSizeIndex={setSelectedLowerLegSizeIndex}
              //
              setSelectedOptions={setSelectedOptions}
              selectedOptions={selectedOptions}
              adviceSize={adviceSize(
                productType,
                sizeVariants,
                selectedOptions,
                node_locale,
              )}
              productVariants={variants}
              setSelectedVariant={setSelectedVariant}
              setAvailibility={setAvailibility}
              setSizeIndex={setSizeIndex}
              setVariantQuantity={setVariantQuantity}
              sizeWizardOpen={sizeWizardOpen}
              productType={productType}
              family={family}
              title={title}
              getPrice={getPrice}
              productSlug={productSlug}
            />
          )}
        </SizeWrapper>
        <ul>
          {productType.menSizeText && gender.name === 'Men'
            ? productType.menSizeText.map((data, i) => (
                <SizeDescription key={data} selected={sizeIndex === i}>
                  {data}
                </SizeDescription>
              ))
            : null}
          {productType.womenSizeText && gender.name === 'Women'
            ? productType.womenSizeText.map((data, i) => (
                <SizeDescription key={data} selected={sizeIndex === i}>
                  {data}
                </SizeDescription>
              ))
            : null}
          {productType.unisexText && gender.name === 'Unisex'
            ? productType.unisexText.map((data, i) => (
                <SizeDescription key={data} selected={sizeIndex === i}>
                  {data}
                </SizeDescription>
              ))
            : null}
        </ul>
      </SizePicker>
      <Controls>
        <StyledQuantityInput>
          <Button
            type='button'
            aria-label='quantity add one less'
            onClick={() => {
              setQuantity(Math.max(1, quantity - 1))
            }}
          >
            -
          </Button>
          <Quantity>{quantity}</Quantity>
          <Button
            type='button'
            aria-label='quantity add one more'
            onClick={() => {
              setQuantity(quantity + 1)
            }}
          >
            +
          </Button>
        </StyledQuantityInput>
        <AddToCart
          type='button'
          onClick={handleAddToCart}
          available={availibility}
          disabled={!availibility && notComingBack}
          className='add-to-cart'
        >
          {availibility ? (
            <>
              <Text load={loading}>
                <span>
                  {availibility
                    ? preorder && variantQuantity <= 0
                      ? preorderATC
                      : addToCart
                    : outOfStock}
                </span>
                &nbsp;-&nbsp;
                {/* Black Friday (uses originalPrice) */}
                {/* {originalPrice ? (
                  <>
                    <DiscountedPriceWrapper
                      color={blackFridayThemeColor || '#FF0058'}
                      visible
                    >
                      <Price shopifyProduct={shopifyProduct} amount={1} />
                    </DiscountedPriceWrapper>
                    <PriceWrapper
                      original
                      strikethrough
                      color={productColorStrikethrough}
                      className={`product-price ${productSlug}`}
                      strikethroughColor={blackFridayThemeColor || '#FF0058'}
                    >
                      <Price
                        shopifyProduct={shopifyProduct}
                        compareAtPrice
                        amount={1}
                      />
                    </PriceWrapper>
                  </>
                ) : ( */}
                {/* <> */}
                {/* Previous setup */}
                {/* <PriceWrapper
                      color={productColorStrikethrough}
                      strikethrough={
                        showTieredDiscountMessage &&
                        !showSpringSaleTieredDiscountMessage &&
                        !onlyGiftProductsInCart &&
                        showDiscount &&
                        !tieredDiscountExclusions &&
                        !removeIndividualTieredDiscountMessage
                      }
                      className={`product-price ${productSlug}`}
                    > */}
                {product?.handle === 'gift-wrapping' ? (
                  <>
                    {node_locale === 'de' || node_locale === 'fr'
                      ? `0 ${currency.symbol}`
                      : `${currency.symbol}0`}
                  </>
                ) : !isSkiing && showAnnouncementBarBlackFriday2022 ? (
                  <>
                    <PriceWrapper textColor={'#FF1E00'}>
                      <Price
                        shopifyProduct={shopifyProduct}
                        amount={1}
                        discount={
                          bigDiscount || quantity > 1
                            ? discountPrice * 0.4
                            : discountPrice * 0.25
                        }
                      />
                    </PriceWrapper>
                    <PriceWrapper
                      strikethrough
                      strikethroughColor={blackFridayThemeColor || '#FF1E00'}
                      color={colorStrikethrough}
                    >
                      <Price shopifyProduct={shopifyProduct} amount={1} />
                    </PriceWrapper>
                  </>
                ) : (
                  <Price
                    shopifyProduct={shopifyProduct}
                    amount='1'
                    selectedVariant={selectedVariant}
                  />
                )}
                {/* </PriceWrapper> */}
                {/* {showTieredDiscountMessage &&
                    !showSpringSaleTieredDiscountMessage &&
                    !onlyGiftProductsInCart && (
                      <DiscountedPriceWrapper
                        color={productColorStrikethrough}
                        visible={
                          !onlyGiftProductsInCart &&
                          tieredDiscountMessage &&
                          showDiscount &&
                          !tieredDiscountExclusions &&
                          !removeIndividualTieredDiscountMessage
                        }
                      >
                        <Price
                          shopifyProduct={shopifyProduct}
                          amount={quantity}
                          discount={discountAmount}
                        />
                      </DiscountedPriceWrapper>
                    )} */}
                {/* </> */}
                {/* )} */}
              </Text>
              <TextLoading load={loading}>
                <span>{addingToCartLoading}</span>
              </TextLoading>
            </>
          ) : (
            <Text outofstock>
              {notComingBack ? notComingBackATC : outOfStockATC}
            </Text>
          )}
        </AddToCart>
        {node_locale === 'de' && amazonUrl && amazonButtonText && (
          <GermanLegalAmazon desktop>
            <RichText json={germanLegal} />
          </GermanLegalAmazon>
        )}
      </Controls>

      {!isSkiing && showAnnouncementBarBlackFriday2022 ? (
        <BFDiscountMessage>
          <RichText json={bFDiscountMessage2022} />
        </BFDiscountMessage>
      ) : (
        !showAnnouncementBarBlackFriday2022 &&
        !removeIndividualTieredDiscountMessage &&
        showTieredDiscountMessage &&
        !tieredDiscountExclusions && (
          <TieredDiscountMessage>{tieredDiscountMessage}</TieredDiscountMessage>
        )
      )}
      <Availability
        available={availibility}
        almostOutOfStock={variantQuantity <= 50}
      >
        {availibility ? (
          variantQuantity > 50 ? (
            <RichText json={inStock} />
          ) : preorder ? (
            preorderMessage
          ) : (
            almostOutOfStock
          )
        ) : outOfStockMessagePerSize ? (
          outOfStockMessagePerSize
        ) : (
          outOfStock
        )}
      </Availability>
      {showBISForm && (
        <Portal id='portal' toggled={showBISForm}>
          <BackInStockForm
            selectedVariant={selectedVariant}
            // primary product
            productId={product?.id}
            showBISForm={showBISForm}
            updateShowBISForm={updateShowBISForm}
          />
        </Portal>
      )}
    </>
  )
}

const BFDiscountMessage = styled.div`
  color: #ff1e00;
  margin-bottom: 4vw;
  font-size: 4vw;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    margin-bottom: 1vw;
    font-size: 1vw;
  }
`

const SizePicker = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: ${(props) => props.theme.space.marginMobile}vw;
  flex-wrap: wrap;

  span {
    font-family: ${(props) => props.theme.fontFamily.bold};
    text-transform: uppercase;
  }

  font-size: ${(props) => props.theme.fontSize.m23};
  letter-spacing: 0.05em;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d12};
    padding-bottom: 1.8vw;
  }
`

const SizeWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 11.875vw;
  ul {
    display: flex;
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    min-height: 2.4305555556vw;
  }
`

const SizeListItem = styled.li`
  display: inline-block;
  text-transform: uppercase;
  font-family: ${(props) => props.theme.fontFamily.bold};

  button {
    border: 1px solid;
    border-color: ${(props) =>
      props.selected ? 'black' : props.theme.colors.mediumGrey};
    background: ${(props) => (props.selected ? 'black' : 'transparent')};
    color: ${(props) => (props.selected ? 'white' : 'black')};
  }
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    &:first-child {
      margin-left: ${(props) => props.theme.space.marginDesktop}vw;
    }
  }
`

const Size = styled.button`
  font-family: ${(props) => props.theme.fontFamily.bold};
  height: ${(props) => props.theme.space.marginMobile * 3}vw;
  width: ${(props) => props.theme.space.marginMobile * 3}vw;
  border-radius: 100%;
  margin-left: 2vw;
  font-size: ${(props) => props.theme.fontSize.m23};
  cursor: pointer;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    margin-left: ${(props) => props.theme.space.marginDesktop}vw;
    height: 2.4305555556vw;
    width: 2.4305555556vw;
    font-size: ${(props) => props.theme.fontSize.d12};
  }
`

const SizeTableButton = styled.button`
  color: black;
  text-decoration: underline;
  margin-left: ${(props) => props.theme.space.marginMobile * 2}vw;
  cursor: pointer;
  font-size: ${(props) => props.theme.fontSize.m23};
  text-transform: uppercase;
  letter-spacing: 0.025em;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    margin-left: ${(props) => props.theme.space.marginDesktop * 2}vw;
    font-size: ${(props) => props.theme.fontSize.d11};
  }
`

const SizeSelectWrapper = styled.div`
  position: relative;
  width: 40vw;
  margin-left: ${(props) => props.theme.space.marginMobile}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    width: 10vw;
    margin-left: ${(props) => props.theme.space.marginDesktop}vw;
  }

  &::after {
    content: '';
    border: solid black;
    border-width: 0 ${(props) => props.theme.space.marginMobile / 8}vw
      ${(props) => props.theme.space.marginMobile / 8}vw 0;
    display: block;
    width: ${(props) => props.theme.space.marginMobile / 3}vw;
    height: ${(props) => props.theme.space.marginMobile / 3}vw;
    transform: rotate(45deg);
    position: absolute;
    right: ${(props) => props.theme.space.marginMobile}vw;
    top: 4.6vw;
    pointer-events: none;

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      border-width: 0 ${(props) => props.theme.space.marginDesktop / 8}vw
        ${(props) => props.theme.space.marginDesktop / 8}vw 0;
      width: ${(props) => props.theme.space.marginDesktop / 2}vw;
      height: ${(props) => props.theme.space.marginDesktop / 2}vw;
      right: ${(props) => props.theme.space.marginDesktop * 2}vw;
      top: 1.7vw;
    }
  }

  @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}),
    screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}) {
    &::after {
      display: none;
    }
  }
`

const SizeSelect = styled.select`
  display: inline-block;
  width: 100%;
  height: 12vw;
  padding: 0 ${(props) => props.theme.space.marginMobile}vw;
  text-transform: uppercase;
  font-family: ${(props) => props.theme.fontFamily.normal};
  border: 1px solid #e0dedf;
  background: white;
  border-radius: 0;
  font-size: inherit;
  -webkit-appearance: none;
  cursor: pointer;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    height: 4vw;
    background: transparent;
    padding: 0 ${(props) => props.theme.space.marginDesktop * 2}vw;
  }
`

const SizeDescription = styled.li`
  font-size: ${(props) => props.theme.fontSize.m23};
  letter-spacing: 0.05em;
  color: #2b2b2b;
  display: ${(props) => (props.selected ? 'block' : 'none')};
  padding-top: ${(props) => props.theme.space.marginMobile}vw;
  line-height: 1.3;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d12};
    padding-top: ${(props) => props.theme.space.marginDesktop * 2}vw;
    line-height: 1.4;
  }
`

const GiftType = styled.span`
  text-transform: capitalize;
`

export const Controls = styled.div`
  display: flex;
  height: ${(props) => props.theme.space.marginMobile * 4}vw;
  margin-bottom: ${(props) => props.theme.space.marginMobile}vw;
  position: relative;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    max-width: 27vw;
    height: 4.2vw;
    margin-bottom: ${(props) => props.theme.space.marginDesktop * 2}vw;
  }
`

const StyledQuantityInput = styled.div`
  border: 1px solid ${(props) => props.theme.colors.mediumGrey};
  display: flex;
  flex: 1;
  height: calc(100% - 2px);
  align-items: center;
`

const Button = styled.button`
  font-family: ${(props) => props.theme.fontFamily.normal};
  font-size: ${(props) => props.theme.fontSize.m26};
  height: 100%;
  flex: 1;
  color: ${(props) => props.theme.colors.grey};
  cursor: pointer;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d14};
  }
`

const Quantity = styled.div`
  font-family: ${(props) => props.theme.fontFamily.bold};
  font-size: ${(props) => props.theme.fontSize.m26};
  color: ${(props) => props.theme.colors.black};
  text-align: center;
  width: ${(props) => props.theme.space.marginMobile * 2}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d14};
    width: ${(props) => props.theme.space.marginDesktop * 2}vw;
  }
`

const AddToCart = styled.button`
  color: ${(props) => (props.available ? 'white' : props.theme.colors.blue)};
  background: ${(props) =>
    props.available ? props.theme.colors.blue : 'transparent'};
  border: ${(props) =>
    props.available ? 'none' : `1px solid ${props.theme.colors.blue}`};
  flex: 2;
  cursor: pointer;
  font-size: ${(props) => props.theme.fontSize.m26};
  margin-left: ${(props) => props.theme.space.marginMobile}vw;
  letter-spacing: 0.025em;

  &:disabled {
    opacity: 0.2;
    cursor: no-drop;
  }
  span {
    &:first-of-type {
      display: ${(props) => (props.loading ? 'none' : 'inline-block')};
    }
    &:nth-of-type(2) {
      display: ${(props) => (props.loading ? 'block' : 'none')};
    }
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d14};
    margin-left: ${(props) => props.theme.space.marginDesktop * 2}vw;
  }
`
const Text = styled.div`
  display: ${(props) => (props.load ? 'none' : 'inline-block')};
  position: relative;
  font-size: ${(props) => (props.outofstock ? '3.2vw' : 'inherit')};
  padding: ${(props) => (props.outofstock ? `0 ${mobileVW(20)}` : '0')};

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => (props.outofstock ? '0.9vw' : 'inherit')};
    padding: ${(props) => (props.outofstock ? `0 ${desktopVW(30)}` : '0')};
  }

  span {
    font-family: ${(props) => props.theme.fontFamily.medium};
  }
`

const fadeIn = keyframes`
  from {opacity: 0;}
  to {opacity: 1;}
`

const PriceWrapper = styled.div`
  display: inline-block;
  position: relative;
  color: ${({ textColor }) => textColor || '#fff'};
  white-space: nowrap;

  ${(props) =>
    props.strikethrough
      ? css`
          top: -4vw;
          position: absolute;
          right: 0;
          @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
            top: -1vw;
          }
        `
      : ''};

  &::after {
    content: '';
    display: ${(props) => (props.strikethrough ? 'block' : 'none')};
    position: absolute;
    left: -0.25em;
    right: -0.25em;
    top: 50%;
    border-top: 1px solid
      ${({ strikethroughColor }) => strikethroughColor || '#FF0058'};
    transform: rotate(-10deg);
    animation: ${fadeIn} 0.8s 0.6s ${(props) => props.theme.easeOut.expo} both;
  }
`

const DiscountedPriceWrapper = styled.div`
  position: absolute;
  display: ${(props) => (props.visible ? 'inline-block' : 'none')};
  font-size: ${(props) => props.theme.fontSize.m26};
  font-weight: 600;
  text-align: right;
  white-space: nowrap;
  color: ${({ color }) => (color ? color : '#fff')};
  animation: ${fadeIn} 0.8s 0.6s ${(props) => props.theme.easeOut.expo} both;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d14};
  }
`

const TextLoading = styled.div`
  display: ${(props) => (props.load ? 'inline-block' : 'none')};

  span {
    font-family: ${(props) => props.theme.fontFamily.medium};
  }
`

export const GermanLegalAmazon = styled.div`
  display: ${(props) => (props.desktop ? 'none' : 'block')};
  font-size: ${(props) => props.theme.fontSize.m23};
  color: #2b2b2b;
  letter-spacing: 0.025em;
  padding-bottom: ${(props) => props.theme.space.marginMobile}vw;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: ${(props) => (props.desktop ? 'block' : 'none')};
    color: ${(props) => props.theme.colors.grey};
    font-size: ${(props) => props.theme.fontSize.d12};
    position: absolute;
    top: 50%;
    right: -${(props) => props.theme.space.marginDesktop * 2}vw;
    transform: translate(100%, -50%);
    padding: 0;
  }
`

const AmazonButton = styled.a`
  height: 85%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  color: #000;
  background: #e4af4a;
  font-size: ${(props) => props.theme.fontSize.m26};
  cursor: pointer;
  letter-spacing: 0.025em;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d14};
  }

  p {
    position: relative;
    top: 0.4vw;
    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      top: 0.15vw;
    }
  }

  svg {
    width: 16vw;
    margin-left: 1.5vw;
    position: relative;
    top: 1.25vw;

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      width: 4.3vw;
      margin-left: 0.4vw;
      top: 0.4vw;
    }
  }
`

const AmazonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`

const TieredDiscountMessage = styled.p`
  text-transform: uppercase;
  color: ${(props) => props.theme.colors.blue};
  font-weight: bold;
  font-size: ${(props) => props.theme.fontSize.m26};
  letter-spacing: 0.08em;
  margin-bottom: ${(props) => props.theme.space.marginMobile}vw;
  margin-left: -0.4vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d15};
    margin-bottom: ${(props) => props.theme.space.marginDesktop * 2}vw;
    margin-left: -0.1vw;
  }
`

const Availability = styled.div`
  font-size: ${(props) => props.theme.fontSize.m23};
  display: flex;
  align-items: center;
  padding-bottom: ${(props) => props.theme.space.marginMobile * 1.5}vw;
  letter-spacing: 0.025em;
  margin-right: ${(props) => props.theme.space.marginMobile}vw;

  a {
    text-decoration: underline;
  }

  &::before {
    content: '';
    display: block;
    margin-top: -2px;
    margin-right: ${(props) => props.theme.space.marginMobile / 2}vw;
    height: ${(props) => props.theme.space.marginMobile / 2}vw;
    width: ${(props) => props.theme.space.marginMobile / 2}vw;
    background: ${(props) =>
      props.available
        ? props.almostOutOfStock
          ? '#eb8900'
          : props.theme.colors.green
        : '#8b2527'};
    border-radius: 100%;
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d12};
    letter-spacing: 0.025em;
    color: #2b2b2b;
    padding-bottom: ${(props) => props.theme.space.marginDesktop * 2}vw;
    margin-right: ${(props) => props.theme.space.marginDesktop * 2}vw;

    &::before {
      content: '';
      margin-top: 0;
      margin-right: ${(props) => props.theme.space.marginDesktop}vw;
      height: ${(props) => props.theme.space.marginDesktop / 2}vw;
      width: ${(props) => props.theme.space.marginDesktop / 2}vw;
    }
  }
`

export default ProductForm
